import { useMemo, useRef } from "preact/hooks";
import { retry as retryIcon, thumbsDown, thumbsDownFill, thumbsUp, thumbsUpFill, warning } from "../icons.ts";
import { interceptLinks } from "../lib/links.ts";
import { renderMarkdown } from "../lib/markdown.ts";
import type { Card, FollowUp, Message, PropertyCard, Rating } from "../lib/types.ts";
import { Cards } from "./Cards.tsx";
import { Icon } from "./Icon.tsx";
import styles from "./Messages.module.css";

interface Props {
  messages: Message[];
  onRetry(id: string): void;
  onCard(card: Card): void;
  onPlan(card: PropertyCard): void;
  /** "Umów prezentację" from a floor plan. */
  onLead(card: PropertyCard): void;
  onFollowUp(followUp: FollowUp, index: number): void;
  onRate(id: string, rating: Rating): void;
  /** Next to the dots while the first words are on their way ("Sprawdzam aktualną ofertę…"). */
  waitingText: string;
  /** No buttons while an answer is on its way. */
  busy: boolean;
}

export function Messages({ messages, onRetry, onCard, onPlan, onLead, onFollowUp, onRate, waitingText, busy }: Props) {
  // Messages restored after moving to another page appear at once; only new ones animate in.
  const restored = useRef(new Set(messages.map((m) => m.id)));
  const lastId = messages.at(-1)?.id;
  return (
    <ol class={styles.list} aria-label="Rozmowa">
      {messages.map((m) =>
        m.role === "user" ? (
          <Question key={m.id} message={m} onRetry={onRetry} animate={!restored.current.has(m.id)} />
        ) : (
          <Answer
            key={m.id}
            message={m}
            last={m.id === lastId}
            onCard={onCard}
            onPlan={onPlan}
            onLead={onLead}
            followUps={m.id === lastId && !busy ? m.followUps : undefined}
            onFollowUp={onFollowUp}
            onRate={onRate}
            waitingText={waitingText}
          />
        ),
      )}
    </ol>
  );
}

function Question({ message, onRetry, animate }: { message: Message; onRetry(id: string): void; animate: boolean }) {
  return (
    <li class={styles.user} data-animate={animate || undefined}>
      <p class={styles.bubble} data-failed={message.status === "failed" || undefined}>
        <span class="visually-hidden">Ty: </span>
        {message.content}
      </p>
      {message.status === "failed" && (
        <p class={styles.failure} role="alert">
          <Icon svg={warning} />
          <span>{message.error}</span>
          {message.retryable && (
            <button type="button" class={styles.retry} onClick={() => onRetry(message.id)}>
              <Icon svg={retryIcon} />
              Wyślij ponownie
            </button>
          )}
        </p>
      )}
    </li>
  );
}

interface AnswerProps {
  message: Message;
  last: boolean;
  onCard(card: Card): void;
  onPlan(card: PropertyCard): void;
  onLead(card: PropertyCard): void;
  followUps?: FollowUp[];
  onFollowUp(followUp: FollowUp, index: number): void;
  onRate(id: string, rating: Rating): void;
  waitingText: string;
}

function Answer({ message, last, onCard, onPlan, onLead, followUps, onFollowUp, onRate, waitingText }: AnswerProps) {
  const html = useMemo(() => (message.content ? renderMarkdown(message.content) : ""), [message.content]);
  const waiting = message.status === "streaming" && !message.content;
  // Thumbs under the latest finished answer; an earlier one keeps them once rated.
  const rateable = Boolean(message.serverId && message.content && !message.status && (last || message.rating));
  return (
    <li class={styles.assistant} aria-busy={message.status === "streaming" || undefined}>
      <span class="visually-hidden">Asystent: </span>
      {waiting ? (
        <span class={styles.waiting} role="status">
          <span class={styles.typing} aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
          <span class={styles.waitingText}>{waitingText}</span>
        </span>
      ) : (
        <div class={styles.prose} onClick={interceptLinks} dangerouslySetInnerHTML={{ __html: html }} />
      )}
      {message.error && message.status !== "failed" && <p class={styles.note}>{message.error}</p>}
      {message.cards && <Cards cards={message.cards} onOpen={onCard} onPlan={onPlan} onLead={onLead} />}
      {rateable && <Rate message={message} onRate={onRate} />}
      {followUps && followUps.length > 0 && (
        <div class={styles.followUps} role="group" aria-label="Co dalej">
          {followUps.map((f, i) => (
            <button
              key={f.label}
              type="button"
              class={styles.followUp}
              data-lead={f.action === "lead" || undefined}
              style={{ "--i": i }}
              onClick={() => onFollowUp(f, i)}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}
    </li>
  );
}

function Rate({ message, onRate }: { message: Message; onRate(id: string, rating: Rating): void }) {
  const rating = message.rating;
  return (
    <div class={styles.rating} role="group" aria-label="Czy ta odpowiedź pomogła?">
      <button
        type="button"
        class={styles.rate}
        aria-pressed={rating === "up"}
        aria-label="Pomocna odpowiedź"
        title="Pomocna"
        onClick={() => onRate(message.id, "up")}
      >
        <Icon svg={rating === "up" ? thumbsUpFill : thumbsUp} />
      </button>
      <button
        type="button"
        class={styles.rate}
        aria-pressed={rating === "down"}
        aria-label="Niepomocna odpowiedź"
        title="Niepomocna"
        onClick={() => onRate(message.id, "down")}
      >
        <Icon svg={rating === "down" ? thumbsDownFill : thumbsDown} />
      </button>
      {rating && (
        <span class={styles.thanks} role="status">
          Dzięki za ocenę
        </span>
      )}
    </div>
  );
}
